import {inject, Injectable} from '@angular/core';
import {InvitationOfferOverviewResponse} from '@frontend/shared-api';
import {ProviderOverallResult} from '../../../types/overall-results.types';
import {OverallResultsCostService} from '../../../services/overall-results-cost.service';
import {
  DetailedBreakdownRow,
  ProviderCostComparisonVm,
  YearlyCostBreakdown,
} from '../cost-comparison.types';

@Injectable({
  providedIn: 'root',
})
export class CostComparisonService {
  private readonly baseHues = [190, 224, 276, 26, 348, 132, 48];
  private readonly overallResultsCostService = inject(OverallResultsCostService);

  buildProviderCostCards(
    providerResults: ProviderOverallResult[],
    overviewsByToolId: Map<string, InvitationOfferOverviewResponse>,
  ): ProviderCostComparisonVm[] {
    return providerResults.map((providerResult) => {
      const hrToolDirectusId = providerResult.hrTool.id.toString();
      const overview = overviewsByToolId.get(hrToolDirectusId);
      const yearsInScope = Math.max(overview?.yearsInScope ?? 0, 0);
      const years = Array.from({length: yearsInScope}, (_, index) => index + 1);
      const yearLabels = years.map((year) => `Y${year}`);

      const yearlyBreakdown = this.buildYearlyCostBreakdown(overview, yearsInScope);
      const totalCost = this.overallResultsCostService.getTotalCost(providerResult);
      const currencyData = this.overallResultsCostService.getCurrencyData(providerResult);
      const headcount = this.getTotalHeadcount(overview);
      const avgYearlyCost = yearsInScope > 0 ? totalCost / yearsInScope : 0;
      const costPerEmployeePerYear = headcount > 0 ? avgYearlyCost / headcount : 0;
      const implementationCostsTotal = this.sum(yearlyBreakdown.implementationCosts);
      const integrationCostsTotal = this.sum(yearlyBreakdown.integrationCosts);
      const otherCostsTotal = this.sum(yearlyBreakdown.otherCosts);
      const serviceFeesTotal = this.sum(yearlyBreakdown.serviceFees);
      const implementationHoursTotal = this.getImplementationHoursTotal(overview);
      const firstYearCashOut = yearlyBreakdown.total[0] ?? 0;
      const oneTimeCostsTotal = implementationCostsTotal + integrationCostsTotal + otherCostsTotal;

      const detailedRows: DetailedBreakdownRow[] = [
        {
          labelKey: 'project.steps.rfq.serviceFees',
          values: yearlyBreakdown.serviceFees,
          total: serviceFeesTotal,
        },
        {
          labelKey: 'project.steps.rfq.implementationFees',
          values: yearlyBreakdown.implementationCosts,
          total: implementationCostsTotal,
        },
        {
          labelKey: 'project.steps.rfq.integrationCosts',
          values: yearlyBreakdown.integrationCosts,
          total: integrationCostsTotal,
        },
        {
          labelKey: 'project.steps.rfq.otherCosts',
          values: yearlyBreakdown.otherCosts,
          total: otherCostsTotal,
        },
        {
          labelKey: 'common.total',
          values: yearlyBreakdown.total,
          total: this.sum(yearlyBreakdown.total),
        },
      ];

      return {
        hrToolDirectusId,
        translatedName: providerResult.translatedName,
        totalCost,
        currencyLocale: currencyData.locale,
        currencyCode: currencyData.currencyCode,
        years,
        yearLabels,
        metrics: [
          {
            labelKey: 'project.steps.overallResults.costComparison.metrics.averageAnnualCost',
            tooltipKey:
              'project.steps.overallResults.costComparison.metrics.averageAnnualCost.tooltip',
            type: 'currency',
            value: avgYearlyCost,
            icon: 'pi pi-chart-line',
            iconAccent: 'cyan',
          },
          {
            labelKey: 'project.steps.overallResults.costComparison.metrics.costPerEmployeePerYear',
            tooltipKey:
              'project.steps.overallResults.costComparison.metrics.costPerEmployeePerYear.tooltip',
            type: 'currency',
            value: costPerEmployeePerYear,
            icon: 'pi pi-users',
            iconAccent: 'green',
          },
          {
            labelKey: 'project.steps.overallResults.costComparison.metrics.costPerEmployeePerMonth',
            tooltipKey:
              'project.steps.overallResults.costComparison.metrics.costPerEmployeePerMonth.tooltip',
            type: 'currency',
            value: costPerEmployeePerYear / 12,
            icon: 'pi pi-calendar',
            iconAccent: 'violet',
          },
          {
            labelKey:
              'project.steps.overallResults.costComparison.metrics.implementationCostProportion',
            tooltipKey:
              'project.steps.overallResults.costComparison.metrics.implementationCostProportion.tooltip',
            type: 'percentage',
            value: this.getPercentage(implementationCostsTotal, totalCost),
            icon: 'pi pi-cog',
            iconAccent: 'amber',
          },
          {
            labelKey:
              'project.steps.overallResults.costComparison.metrics.integrationCostProportion',
            tooltipKey:
              'project.steps.overallResults.costComparison.metrics.integrationCostProportion.tooltip',
            type: 'percentage',
            value: this.getPercentage(integrationCostsTotal, totalCost),
            icon: 'pi pi-link',
            iconAccent: 'violet',
          },
          {
            labelKey:
              'project.steps.overallResults.costComparison.metrics.totalImplementationHours',
            tooltipKey:
              'project.steps.overallResults.costComparison.metrics.totalImplementationHours.tooltip',
            type: 'hours',
            value: implementationHoursTotal,
            icon: 'pi pi-clock',
            iconAccent: 'cyan',
          },
          {
            labelKey: 'project.steps.overallResults.costComparison.metrics.serviceFeeProportion',
            tooltipKey:
              'project.steps.overallResults.costComparison.metrics.serviceFeeProportion.tooltip',
            type: 'percentage',
            value: this.getPercentage(serviceFeesTotal, totalCost),
            icon: 'pi pi-bolt',
            iconAccent: 'green',
          },
          {
            labelKey: 'project.steps.overallResults.costComparison.metrics.cashOutYearOne',
            tooltipKey:
              'project.steps.overallResults.costComparison.metrics.cashOutYearOne.tooltip',
            type: 'currency',
            value: firstYearCashOut,
            icon: 'pi pi-wallet',
            iconAccent: 'red',
          },
          {
            labelKey: 'project.steps.overallResults.costComparison.metrics.oneTimeCostProportion',
            tooltipKey:
              'project.steps.overallResults.costComparison.metrics.oneTimeCostProportion.tooltip',
            type: 'percentage',
            value: this.getPercentage(oneTimeCostsTotal, totalCost),
            icon: 'pi pi-percentage',
            iconAccent: 'amber',
          },
        ],
        yearlyBreakdown,
        detailedRows,
      };
    });
  }

  sum(values: number[]): number {
    return values.reduce((sum, value) => sum + value, 0);
  }

  getProviderPalette(index: number): {
    serviceFees: string;
    implementationCosts: string;
    integrationCosts: string;
    otherCosts: string;
  } {
    const hue = this.baseHues[index % this.baseHues.length];
    return {
      serviceFees: `hsl(${hue} 70% 58%)`,
      implementationCosts: `hsl(${hue} 70% 50%)`,
      integrationCosts: `hsl(${hue} 70% 42%)`,
      otherCosts: `hsl(${hue} 70% 34%)`,
    };
  }

  private buildYearlyCostBreakdown(
    overview: InvitationOfferOverviewResponse | undefined,
    yearsInScope: number,
  ): YearlyCostBreakdown {
    if (!overview || yearsInScope <= 0) {
      return {
        serviceFees: [],
        implementationCosts: [],
        integrationCosts: [],
        otherCosts: [],
        total: [],
      };
    }

    const isZeroBasedIndexing = this.detectZeroBasedIndexing(overview);
    const serviceFeesPerYear = this.getServiceFeesPerYear(overview, yearsInScope);
    const implementationCostsPerYear = this.getImplementationCostsPerYear(
      overview,
      yearsInScope,
      isZeroBasedIndexing,
    );
    const integrationCostsPerYear = this.getIndexedYearlyTotals(
      yearsInScope,
      overview.integrationSetupFeesResponse.flatMap((item) => item.yearlyItemFees ?? []),
      isZeroBasedIndexing,
    );
    const otherCostsPerYear = this.getIndexedYearlyTotals(
      yearsInScope,
      overview.integrationOtherFeesResponse.flatMap((item) => item.yearlyItemFees ?? []),
      isZeroBasedIndexing,
    );
    const total = Array.from({length: yearsInScope}, (_, index) => {
      return (
        (serviceFeesPerYear[index] ?? 0) +
        (implementationCostsPerYear[index] ?? 0) +
        (integrationCostsPerYear[index] ?? 0) +
        (otherCostsPerYear[index] ?? 0)
      );
    });

    return {
      serviceFees: serviceFeesPerYear,
      implementationCosts: implementationCostsPerYear,
      integrationCosts: integrationCostsPerYear,
      otherCosts: otherCostsPerYear,
      total,
    };
  }

  private getServiceFeesPerYear(
    overview: InvitationOfferOverviewResponse,
    yearsInScope: number,
  ): number[] {
    const yearlyValue = overview.serviceFeesResponse.reduce((sum, fee) => {
      const feePerUnit = Number(fee.feePerUnit);
      const eligibleHeadcount = Number(fee.eligibleHeadcount);
      if (Number.isNaN(feePerUnit) || Number.isNaN(eligibleHeadcount)) {
        return sum;
      }
      return sum + feePerUnit * eligibleHeadcount;
    }, 0);
    return Array.from({length: yearsInScope}, () => yearlyValue);
  }

  private getImplementationCostsPerYear(
    overview: InvitationOfferOverviewResponse,
    yearsInScope: number,
    isZeroBasedIndexing: boolean,
  ): number[] {
    const yearlyCosts = Array.from({length: yearsInScope}, () => 0);
    overview.implementationActivityFeesResponse.forEach((activity) => {
      const hourlyRate = Number(activity.hourlyRate) || 0;
      if (hourlyRate <= 0) {
        return;
      }
      const yearlyHours = Array.from({length: yearsInScope}, () => 0);
      activity.implementationFunctionFeesResponse.forEach((functionFee) => {
        functionFee.implementationYearlyFees.forEach((yearlyFee, indexPosition) => {
          const yearIndex = this.resolveYearIndex(
            yearlyFee.index,
            yearsInScope,
            isZeroBasedIndexing,
            indexPosition,
          );
          if (yearIndex < 0 || yearIndex >= yearsInScope) {
            return;
          }
          yearlyHours[yearIndex] += Number(yearlyFee.feePerUnit) || 0;
        });
      });

      yearlyHours.forEach((hours, index) => {
        yearlyCosts[index] += hours * hourlyRate;
      });
    });
    return yearlyCosts;
  }

  private getImplementationHoursTotal(
    overview: InvitationOfferOverviewResponse | undefined,
  ): number {
    if (!overview) {
      return 0;
    }
    return overview.implementationActivityFeesResponse.reduce((sum, activity) => {
      const activityHours = activity.implementationFunctionFeesResponse.reduce(
        (activitySum, functionFee) =>
          activitySum +
          functionFee.implementationYearlyFees.reduce(
            (functionSum, yearFee) => functionSum + (Number(yearFee.feePerUnit) || 0),
            0,
          ),
        0,
      );
      return sum + activityHours;
    }, 0);
  }

  private getTotalHeadcount(overview: InvitationOfferOverviewResponse | undefined): number {
    if (!overview) {
      return 0;
    }
    return overview.companyInfosResponse.reduce((sum, companyInfo) => {
      return (
        sum +
        (companyInfo.salariedHeadcount ?? 0) +
        (companyInfo.industrialHeadcount ?? 0) +
        (companyInfo.contingentHeadcount ?? 0)
      );
    }, 0);
  }

  private getIndexedYearlyTotals(
    yearsInScope: number,
    yearlyFees: Array<{index: number; feePerUnit: number}>,
    isZeroBasedIndexing: boolean,
  ): number[] {
    const result = Array.from({length: yearsInScope}, () => 0);
    yearlyFees.forEach((yearlyFee, indexPosition) => {
      const yearIndex = this.resolveYearIndex(
        yearlyFee.index,
        yearsInScope,
        isZeroBasedIndexing,
        indexPosition,
      );
      if (yearIndex < 0 || yearIndex >= yearsInScope) {
        return;
      }
      result[yearIndex] += Number(yearlyFee.feePerUnit) || 0;
    });
    return result;
  }

  private detectZeroBasedIndexing(overview: InvitationOfferOverviewResponse): boolean {
    const indices: number[] = [
      ...overview.integrationSetupFeesResponse.flatMap((item) =>
        (item.yearlyItemFees ?? []).map((fee) => fee.index),
      ),
      ...overview.integrationOtherFeesResponse.flatMap((item) =>
        (item.yearlyItemFees ?? []).map((fee) => fee.index),
      ),
      ...overview.implementationActivityFeesResponse.flatMap((activity) =>
        (activity.implementationFunctionFeesResponse ?? []).flatMap((functionFee) =>
          (functionFee.implementationYearlyFees ?? []).map((fee) => fee.index),
        ),
      ),
    ];

    return indices.some((indexValue) => indexValue === 0);
  }

  private resolveYearIndex(
    rawIndex: number,
    yearsInScope: number,
    isZeroBasedIndexing: boolean,
    fallbackPosition: number,
  ): number {
    const normalizedIndex = Number(rawIndex);
    if (!Number.isNaN(normalizedIndex)) {
      const yearIndex = isZeroBasedIndexing ? normalizedIndex : normalizedIndex - 1;
      if (yearIndex >= 0 && yearIndex < yearsInScope) {
        return yearIndex;
      }
    }

    return fallbackPosition >= 0 && fallbackPosition < yearsInScope ? fallbackPosition : -1;
  }

  private getPercentage(value: number, total: number): number {
    if (total <= 0) {
      return 0;
    }
    return (value / total) * 100;
  }
}
